import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
// Import the video file
import servicesVideo from "../assets/Home.mp4";

const servicesData = [
  {
    id: 1,
    title: "Workday Advisory",
    icon: "🎯",
    description: "Strategic guidance and roadmap planning for successful Workday implementation.",
    color: "linear-gradient(135deg, #0073ff 0%, #2980b9 100%)",
    detailedDescription: "Our Workday Advisory services provide expert strategic guidance throughout your Workday journey. We help you develop a comprehensive roadmap aligned with your business objectives, ensuring maximum ROI from your Workday investment. Our certified advisors bring deep industry expertise to optimize your HCM, Financials, and Planning processes.",
    features: [
      "Strategic Implementation Planning",
      "Business Process Optimization",
      "Roadmap & Future State Planning",
      "ROI Analysis & Business Case",
      "Change Management Strategy",
      "System Architecture Design"
    ],
    benefits: [
      "40% faster implementation",
      "60% risk reduction",
      "Optimized business processes",
      "Increased stakeholder alignment"
    ],
    caseStudy: "Guided Fortune 500 retail company through global Workday implementation, achieving 40% faster deployment and 99% user satisfaction."
  },
  {
    id: 2,
    title: "Workday AMS",
    icon: "🛡️",
    description: "24/7 Application Management Services for continuous optimization.",
    color: "linear-gradient(135deg, #3498db 0%, #2c3e50 100%)",
    detailedDescription: "Our Workday AMS provides comprehensive support and maintenance services to ensure your Workday system runs smoothly 24/7. We offer proactive monitoring, incident management, and continuous optimization to keep your operations running at peak performance.",
    features: [
      "24/7 Production Support",
      "Incident & Problem Management",
      "Enhancement & Updates",
      "Security & Compliance",
      "Performance Monitoring",
      "Backup & Disaster Recovery"
    ],
    benefits: [
      "99.9% system uptime",
      "50% faster resolution",
      "Proactive issue prevention",
      "Reduced total cost of ownership"
    ],
    caseStudy: "Managed AMS for financial services firm with 30,000+ users, achieving 99.95% uptime and 40% cost reduction."
  },
  {
    id: 3,
    title: "Workday Experts on Demand",
    icon: "👥",
    description: "Flexible access to certified Workday experts for your specific needs.",
    color: "linear-gradient(135deg, #1abc9c 0%, #16a085 100%)",
    detailedDescription: "Get immediate access to certified Workday experts for your specific project needs. Our flexible engagement model allows you to scale resources up or down based on your requirements, ensuring you have the right expertise at the right time.",
    features: [
      "Certified Workday Consultants",
      "Flexible Engagement Models",
      "Specialized Expertise Access",
      "Project-Based Staffing",
      "Knowledge Transfer",
      "Mentoring & Coaching"
    ],
    benefits: [
      "On-demand expertise",
      "Reduced hiring costs",
      "Faster project completion",
      "Access to niche skills"
    ],
    caseStudy: "Provided expert resources for healthcare client's Workday upgrade, completing project 40% under budget."
  },
  {
    id: 4,
    title: "Workday Test Automation",
    icon: "🤖",
    description: "Automated testing solutions ensuring quality and speed.",
    color: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
    detailedDescription: "Accelerate your Workday deployments with our comprehensive test automation solutions. We ensure quality, reduce risks, and enable continuous delivery through automated testing frameworks specifically designed for Workday environments.",
    features: [
      "End-to-End Regression Testing",
      "Performance & Load Testing",
      "Integration Testing",
      "Continuous Testing Pipeline",
      "Test Data Management",
      "Defect Tracking & Reporting"
    ],
    benefits: [
      "80% faster testing cycles",
      "95% test automation coverage",
      "Early defect detection",
      "Reusable test assets"
    ],
    caseStudy: "Implemented test automation for manufacturing client, reducing deployment cycles from 3 months to 3 weeks."
  },
  {
    id: 5,
    title: "Workday Certifications",
    icon: "🏆",
    description: "Official Workday certification programs for your team.",
    color: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)",
    detailedDescription: "Get your team certified with official Workday certification programs. We provide comprehensive training and certification support for various Workday modules, ensuring your organization has the certified expertise needed for success.",
    features: [
      "Workday Pro Certification",
      "HCM Core Certification",
      "Financials Certification",
      "Reporting Certification",
      "Integration Certification",
      "Security Certification"
    ],
    benefits: [
      "Official Workday certifications",
      "Increased system proficiency",
      "Better support quality",
      "Enhanced career development"
    ],
    caseStudy: "Certified 100+ consultants for global consulting firm, achieving 95% certification success rate."
  },
  {
    id: 6,
    title: "Workday Training",
    icon: "📚",
    description: "Comprehensive training programs for all user levels.",
    color: "linear-gradient(135deg, #f39c12 0%, #d35400 100%)",
    detailedDescription: "Empower your team with comprehensive Workday training programs tailored for all user levels. From administrators to end-users, we offer customized training solutions that ensure maximum adoption and ROI from your Workday investment.",
    features: [
      "Administrator Training",
      "End-User Workshops",
      "Train-the-Trainer Programs",
      "Customized Curriculum",
      "eLearning Modules",
      "Ongoing Support"
    ],
    benefits: [
      "95% user adoption",
      "70% fewer support tickets",
      "Internal expertise building",
      "Continuous learning"
    ],
    caseStudy: "Trained 10,000+ users across global organization, achieving 98% adoption within first quarter."
  }
];

const Services = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="services-page">
      {/* ===== HERO SECTION ===== */}
      <section className="services-hero">
        {/* Full Height Video Background */}
        <div className="hero-video-container">
          <video 
            className="hero-background-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={servicesVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="video-overlay"></div>
        </div>
        
        {/* Background Orbs - now with higher z-index */}
        <div className="background-orb orb-1"></div>
        <div className="background-orb orb-2"></div>
        <div className="background-orb orb-3"></div>
        
        <div className="services-hero-container" data-animate="fade-up">
          <div className="hero-content">
            <div className="breadcrumb">
              <span className="breadcrumb-item" onClick={() => navigate("/")}>Home</span>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-item active">Services</span>
            </div>
            
            <h1 className="hero-title">
              End-to-End Workday Services by Experts
            </h1>
            
            <p className="hero-subtitle">
              Comprehensive Workday solutions designed to maximize your investment, 
              optimize operations, and drive digital transformation across your organization.
            </p>
            
          <div
  className="hero-stats"
  style={{
    display: "flex",
    gap: "40px",
    marginTop: "32px",
    flexWrap: "wrap",
  }}
>
  {/* STAT 1 */}
  <div
    className="hero-stat"
    style={{
      textAlign: "center",
    }}
  >
    <div
      className="stat-number"
      style={{
        color: "#ffffff",
        fontSize: "42px",
        fontWeight: "800",
        letterSpacing: "0.8px",
        textShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}
    >
      50+
    </div>
    <div
      className="stat-label"
      style={{
        color: "rgba(255,255,255,0.85)",
        fontSize: "15px",
        fontWeight: "600",
        marginTop: "6px",
      }}
    >
      Projects Completed
    </div>
  </div>

  {/* STAT 2 */}
  <div
    className="hero-stat"
    style={{
      textAlign: "center",
    }}
  >
    <div
      className="stat-number"
      style={{
        color: "#ffffff",
        fontSize: "42px",
        fontWeight: "800",
        letterSpacing: "0.8px",
        textShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}
    >
      99%
    </div>
    <div
      className="stat-label"
      style={{
        color: "rgba(255,255,255,0.85)",
        fontSize: "15px",
        fontWeight: "600",
        marginTop: "6px",
      }}
    >
      Client Satisfaction
    </div>
  </div>

  {/* STAT 3 */}
  <div
    className="hero-stat"
    style={{
      textAlign: "center",
    }}
  >
    <div
      className="stat-number"
      style={{
        color: "#ffffff",
        fontSize: "42px",
        fontWeight: "800",
        letterSpacing: "0.8px",
        textShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}
    >
      50+
    </div>
    <div
      className="stat-label"
      style={{
        color: "rgba(255,255,255,0.85)",
        fontSize: "15px",
        fontWeight: "600",
        marginTop: "6px",
      }}
    >
      Certified Experts
    </div>
  </div>
</div>

          </div>
          
          <div className="hero-visual">
            
            <div className="pulse-ring"></div>
          </div>
        </div>
        
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <div className="scroll-dot"></div>
        </div>
      </section>

      {/* ===== SERVICES GRID ===== */}
      <section className="services-grid-section">
        <div className="section-header" data-animate="fade-up">
          <h2 className="section-title">Our Workday Services</h2>
          <p className="section-subtitle">
            Tailored solutions to meet your unique business requirements and drive success
          </p>
        </div>
        
        <div className="services-grid-container">
          {servicesData.map((service, index) => (
            <div 
              key={service.id} 
              className="service-card"
              data-animate="zoom-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="service-card-inner">
                <div className="service-card-header">
                  <div className="service-icon-wrapper" style={{ background: service.color }}>
                    <span className="service-icon">{service.icon}</span>
                    <div className="icon-glow"></div>
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                </div>
                
                <div className="service-card-body">
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-features">
                    <h4>Key Features:</h4>
                    <ul>
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx}>
                          <span
  className="feature-icon"
  style={{
    backgroundColor: "rgba(13, 219, 252, 0.65)", // dull blue circle
    color: "#fff",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    marginRight: "10px"
  }}
>
  ✓
</span>

                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="service-benefits">
                    <h4>Key Benefits:</h4>
                    <div className="benefits-tags">
                      {service.benefits.map((benefit, idx) => (
                        <span key={idx} className="benefit-tag">{benefit}</span>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    className="btn-service-details"
                    onClick={() => navigate(`/services/${service.id}`)}
                  >
                    <span>Explore Service</span>
                    <span className="btn-arrow">→</span>
                  </button>
                </div>
                
                <div className="service-card-glow" style={{ background: service.color }}></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICE DETAILS ===== */}
      <section className="service-details-section">
        <div className="section-header" data-animate="fade-up">
          <h2 className="section-title">Service Excellence</h2>
          <p className="section-subtitle">
            Deep expertise combined with proven methodologies for guaranteed success
          </p>
        </div>
        
        <div className="service-details-container">
          {servicesData.slice(0, 3).map((service, index) => (
            <div 
              key={service.id} 
              className="service-detail-card"
              data-animate={index % 2 === 0 ? "slide-left" : "slide-right"}
            >
              <div className="detail-card-inner">
                <div className="detail-header">
                  <div className="detail-icon" style={{ background: service.color }}>
                    {service.icon}
                  </div>
                  <div className="detail-title-section">
                    <h3>{service.title}</h3>
                    <div className="detail-tags">
                      <span className="detail-tag">Certified Experts</span>
                      <span className="detail-tag">Proven Methodology</span>
                      <span className="detail-tag">Global Delivery</span>
                    </div>
                  </div>
                </div>
                
                <div className="detail-content">
                  <p className="detail-description">{service.detailedDescription}</p>
                  
                  <div className="detail-grid">
                    <div className="detail-column">
                      <h4>Comprehensive Features</h4>
                      <ul className="feature-list">
                        {service.features.map((feature, idx) => (
                          <li key={idx}>
                            <span className="check-icon">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="detail-column">
                      <h4>Success Metrics</h4>
                      <div className="case-study">
                        <div className="case-icon">📈</div>
                        <p className="case-text">{service.caseStudy}</p>
                        <div className="case-metrics">
                          <div className="metric">
                            <div className="metric-value">95%+</div>
                            <div className="metric-label">Success Rate</div>
                          </div>
                          <div className="metric">
                            <div className="metric-value">40%</div>
                            <div className="metric-label">Faster</div>
                          </div>
                          <div className="metric">
                            <div className="metric-value">99%</div>
                            <div className="metric-label">Satisfaction</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="detail-cta">
                    <button className="btn-detail-primary" onClick={() => navigate("/contact")}>
                      Schedule Consultation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="view-all-cta" data-animate="fade-up">
          <button className="btn-view-all-services" onClick={() => navigate("/services/all")}>
            View All Service Details
          </button>
        </div>
      </section>

      {/* ===== SERVICE COMPARISON ===== */}
      <section className="service-comparison-section">
        <div className="section-header" data-animate="fade-up">
          <h2 className="section-title">Choose the Right Service</h2>
          <p className="section-subtitle">
            Select the service that best fits your organization's needs and objectives
          </p>
        </div>
        
        <div className="comparison-table" data-animate="fade-up">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Best For</th>
                <th>Delivery Model</th>
                <th>Typical Duration</th>
                <th>Key Outcome</th>
              </tr>
            </thead>
            <tbody>
              {servicesData.map((service) => (
                <tr key={service.id}>
                  <td>
                    <div className="service-row">
                      <span className="service-icon-small">{service.icon}</span>
                      {service.title}
                    </div>
                  </td>
                  <td>
                    {service.id === 1 && "Strategic planning & implementation"}
                    {service.id === 2 && "Ongoing support & maintenance"}
                    {service.id === 3 && "Flexible project staffing"}
                    {service.id === 4 && "Quality assurance & testing"}
                    {service.id === 5 && "Team certification & validation"}
                    {service.id === 6 && "User training & adoption"}
                  </td>
                  <td>
                    {service.id <= 2 ? "Managed Service" : "Project-Based"}
                  </td>
                  <td>
                    {service.id <= 2 ? "Ongoing" : "3-6 months"}
                  </td>
                  <td>
                    <span className="outcome-tag">High ROI</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== METHODOLOGY ===== */}
      <section className="methodology-section">
        <div className="methodology-container" data-animate="fade-up">
          <div className="methodology-content">
            <h2 className="methodology-title">Our Engagement Approach</h2>
            <p className="methodology-subtitle">
              A structured methodology ensuring successful delivery and maximum value
            </p>
            
            <div className="methodology-steps">
              <div className="step" data-animate="zoom-in" >
                <div className="step-number">01</div>
                <h3>Discovery & Assessment</h3>
                <p>Comprehensive analysis of requirements and current state</p>
              </div>
              
              <div className="step-connector"></div>
              
              <div className="step" data-animate="zoom-in" style={{ animationDelay: "0.2s" }}>
                <div className="step-number">02</div>
                <h3>Solution Design</h3>
                <p>Tailored solution architecture and planning</p>
              </div>
              
              <div className="step-connector"></div>
              
              <div className="step" data-animate="zoom-in" style={{ animationDelay: "0.4s" }}>
                <div className="step-number">03</div>
                <h3>Implementation</h3>
                <p>Phased delivery with quality assurance</p>
              </div>
              
              <div className="step-connector"></div>
              
              <div className="step" data-animate="zoom-in" style={{ animationDelay: "0.6s" }}>
                <div className="step-number">04</div>
                <h3>Support & Optimize</h3>
                <p>Ongoing support and continuous improvement</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="services-cta-section">
        <div className="cta-container" data-animate="zoom-in">
          <div className="cta-content">
            <div className="cta-icon">🚀</div>
            <h2 style={{ color: "#0d6efd" }}>
  Ready to Transform Your Workday Experience?
</h2>

            <p className="cta-description">
              Connect with our Workday experts to discuss how we can help you achieve 
              your business objectives with our comprehensive suite of services.
            </p>
            
            <div className="cta-buttons">
              <button className="btn-cta-primary" onClick={() => navigate("/contact")}>
                Get Free Consultation
              </button>
            </div>
            
            <div className="cta-stats">
              <div className="cta-stat">
                <div className="cta-stat-number">24/7</div>
                <div className="cta-stat-label">Global Support</div>
              </div>
              <div className="cta-stat">
                <div className="cta-stat-number">50+</div>
                <div className="cta-stat-label">Certified Experts</div>
              </div>
              <div className="cta-stat">
                <div className="cta-stat-number">99%</div>
                <div className="cta-stat-label">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;